import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import { useTasksTab } from '../../hooks/Tasks/useTasksTab';
import { TargetType, FieldResourceTargetValue } from './types';
import { TARGET_TYPE_LABELS, TASK_TYPE_TARGET_MAP, GROUP_KEY_TO_TASK_TYPE, PRIMARY_COLOR } from './constants';
import { TaskType } from './types';
import FieldResourceCoverageMap from '../FieldResourceView/Map';
import { tokens } from "../../theme/tokens";

type ViewMode = 'list' | 'map';

type ParsedLine = {
  text: string;
  type: TargetType;
  isDuplicate: boolean;
};

const TYPE_SHORT: Record<TargetType, string> = {
  PHONE: 'Phone',
  EMAIL: 'Email',
  ADDRESS: 'Address',
  PROFILE: 'Profile',
  PERSON: 'Person',
  FIELD_RESOURCE: 'Field Resource',
  OTHER: 'Other',
};

// ─── Field resource picker (shown when selectedType === FIELD_RESOURCE) ────────
const FieldResourcePicker: React.FC<{
  taskId: string;
  viewMode: ViewMode;
  setViewMode: (v: ViewMode) => void;
}> = ({ taskId, viewMode, setViewMode }) => {
  const { assignFieldResource, tasks, fieldResources, fieldResourcesLoading } = useTasksTab();
  const task = tasks.find((t) => t.task_id === taskId);

  const [search, setSearch] = useState('');

  // Collect all resource IDs already assigned as keys across all FIELD_RESOURCE targets
  const assignedIds = new Set(
    (task?.targets ?? [])
      .filter((t) => t.type === 'FIELD_RESOURCE')
      .flatMap((t) => Object.keys((t.value as FieldResourceTargetValue).data ?? {}))
  );

  const filtered = fieldResources.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  // ── View toggle (List | Map) ─────────────────────────────────────────────────
  const toggleStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    fontWeight: active ? 600 : 400,
    color: active ? tokens.color.surface.default : tokens.color.text.secondary,
    backgroundColor: active ? PRIMARY_COLOR : tokens.color.surface.subtle,
    border: active ? `1px solid ${PRIMARY_COLOR}` : `1px solid ${tokens.color.border.default}`,
    borderRadius: '6px',
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <Box>
      {/* List / Map toggle */}
      <Box sx={{ display: 'flex', gap: '6px', mb: '12px' }}>
        <button type="button" style={toggleStyle(viewMode === 'list')} onClick={() => setViewMode('list')}>
          <FormatListBulletedIcon sx={{ fontSize: '14px' }} /> List
        </button>
        <button type="button" style={toggleStyle(viewMode === 'map')} onClick={() => setViewMode('map')}>
          <MapIcon sx={{ fontSize: '14px' }} /> Map
        </button>
      </Box>

      {/* ── List view ─────────────────────────────────────────────────────────── */}
      {viewMode === 'list' && (
        <Box sx={{ height: '560px', display: 'flex', flexDirection: 'column', border: `1px solid ${tokens.color.border.default}`, borderRadius: '8px', p: '12px' }}>
          {/* Search */}
          <Box sx={{ position: 'relative', mb: '10px', flexShrink: 0 }}>
            <SearchIcon sx={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: tokens.color.text.secondary }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search field resources…"
              style={{
                width: '100%',
                fontSize: '13px',
                padding: '8px 10px 8px 34px',
                border: `1px solid ${tokens.color.border.strong}`,
                borderRadius: '7px',
                outline: 'none',
                color: tokens.color.text.primary,
                boxSizing: 'border-box',
              }}
            />
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {fieldResourcesLoading ? (
              <span style={{ fontSize: '12px', color: tokens.color.text.secondary }}>Loading…</span>
            ) : filtered.length === 0 ? (
              <span style={{ fontSize: '12px', color: tokens.color.text.secondary }}>No field resources found.</span>
            ) : (
              filtered.map((resource) => {
                const added = assignedIds.has(resource.id);
                return (
                  <Box
                    key={resource.id}
                    onClick={() => { if (!added) assignFieldResource(taskId, resource.id, resource.name); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      px: '12px',
                      py: '9px',
                      borderRadius: '7px',
                      cursor: added ? 'default' : 'pointer',
                      backgroundColor: added ? tokens.color.workflow.worked.bg : 'transparent',
                      border: added ? `1px solid ${tokens.color.workflow.worked.border}` : '1px solid transparent',
                      mb: '4px',
                      transition: 'background 0.12s ease',
                      '&:hover': { backgroundColor: added ? tokens.color.workflow.worked.bg : tokens.color.surface.sunken },
                    }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: '18px', color: added ? tokens.color.workflow.worked.fg : `${tokens.color.text.secondary}`, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: added ? tokens.color.workflow.worked.fg : tokens.color.text.primary, display: 'block' }}>
                        {resource.name}
                      </span>
                      {resource.address && (
                        <span style={{ fontSize: '11px', color: tokens.color.text.secondary }}>{resource.address}</span>
                      )}
                    </Box>
                    {added && (
                      <span style={{ fontSize: '11px', color: tokens.color.workflow.worked.fg, fontWeight: 600, flexShrink: 0 }}>Added ✓</span>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}

      {/* ── Map view ─────────────────────────────────────────────────────────── */}
      {viewMode === 'map' && (
        <Box sx={{ height: '560px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${tokens.color.border.default}` }}>
          <FieldResourceCoverageMap
            fieldResourceList={fieldResources}
            onAssign={(id, name) => assignFieldResource(taskId, id, name)}
            assignedIds={assignedIds}
            mapHeight="560px"
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Paste area — two-column: textarea left, preview right ────────────────────
const PasteArea: React.FC<{
  selectedType: TargetType;
  rawText: string;
  onRawTextChange: (text: string) => void;
  parsedLines: ParsedLine[];
  onAdd: () => void;
}> = ({ selectedType, rawText, onRawTextChange, parsedLines, onAdd }) => {
  const placeholder =
    selectedType === 'ADDRESS'
      ? '123 Main St, Austin TX\n456 Oak Ave, Dallas TX'
      : selectedType === 'PERSON'
      ? 'John Smith\nJane Doe'
      : selectedType === 'PROFILE'
      ? 'twitter.com/johnsmith\ninstagram.com/john.smith'
      : selectedType === 'EMAIL'
      ? 'john@example.com\njsmith@gmail.com'
      : '+1 555 123 4567\n+1 555 999 0001';

  return (
    <Box sx={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>
      {/* ── Left col: textarea ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, display: 'block', marginBottom: '6px', visibility: 'hidden' }}>
          Preview
        </span>
        <textarea
          autoFocus
          value={rawText}
          onChange={(e) => onRawTextChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onAdd(); }}
          placeholder={placeholder}
          style={{
            display: 'block',
            width: '100%',
            flex: 1,
            minHeight: '160px',
            fontSize: '13px',
            padding: '8px 10px',
            border: `1px solid ${tokens.color.border.strong}`,
            borderRadius: '7px',
            outline: 'none',
            resize: 'none',
            fontFamily: 'monospace',
            color: tokens.color.text.primary,
            boxSizing: 'border-box',
            lineHeight: 1.6,
          }}
        />
        <span style={{ fontSize: '11px', color: tokens.color.text.secondary, marginTop: '5px' }}>
          ⌘ / Ctrl + Enter to add
        </span>
      </Box>

      {/* ── Right col: preview list ────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{
          fontSize: '11px',
          fontWeight: 700,
          color: tokens.color.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '6px',
          display: 'block',
        }}>
          Preview
        </span>
        <Box sx={{
          flex: 1,
          minHeight: '160px',
          overflowY: 'auto',
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: '7px',
          backgroundColor: tokens.color.surface.sunken,
          p: '6px',
        }}>
          {parsedLines.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <span style={{ fontSize: '12px', color: tokens.color.border.strong, textAlign: 'center', lineHeight: 1.6 }}>
                Paste values on the left<br />to preview them here
              </span>
            </Box>
          ) : (
            parsedLines.map((line, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: '8px',
                  py: '5px',
                  borderRadius: '5px',
                  opacity: line.isDuplicate ? 0.65 : 1,
                  mb: '2px',
                  '&:hover': { backgroundColor: tokens.color.surface.subtle },
                }}
              >
                {/* Type badge */}
                <span style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: tokens.color.text.secondary,
                  backgroundColor: tokens.color.surface.subtle,
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: '4px',
                  padding: '1px 5px',
                  flexShrink: 0,
                  letterSpacing: '0.02em',
                }}>
                  {TYPE_SHORT[line.type]}
                </span>
                {/* Value */}
                <span style={{
                  fontSize: '12px',
                  color: line.isDuplicate ? `${tokens.color.text.secondary}` : tokens.color.text.primary,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {line.text}
                </span>
                {/* Duplicate label */}
                {line.isDuplicate && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: tokens.color.workflow.doing.fg,
                    flexShrink: 0,
                  }}>
                    Already exists
                  </span>
                )}
              </Box>
            ))
          )}
        </Box>
        <span style={{ fontSize: '11px', display: 'block', marginTop: '5px', visibility: 'hidden' }}>
          ⌘ / Ctrl + Enter to add
        </span>
      </Box>
    </Box>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────
const PasteTargetsModal: React.FC = () => {
  const { pasteModal, closePasteModal, tasks, pasteTargets } = useTasksTab();
  const { open, taskId } = pasteModal;

  const task = tasks.find((t) => t.task_id === taskId);
  const taskType: TaskType = task?.task_type
    ?? (task?.group_key ? GROUP_KEY_TO_TASK_TYPE[task.group_key] : undefined)
    ?? 'CUSTOM';
  const allowedTypes = TASK_TYPE_TARGET_MAP[taskType];

  const [selectedType, setSelectedType] = useState<TargetType>(allowedTypes[0]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [rawText, setRawText] = useState('');

  useEffect(() => {
    setSelectedType(TASK_TYPE_TARGET_MAP[taskType][0]);
    setRawText('');
  }, [taskId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setViewMode('list');
      setRawText('');
    }
  }, [open]);

  const existingValues = useMemo(() => new Set(
    (task?.targets ?? [])
      .filter((t) => t.type === selectedType)
      .map((t) => {
        if (t.type === 'PERSON') return ((t.value as { name?: string }).name ?? '').toLowerCase();
        return ((t.value as { text?: string }).text ?? '').toLowerCase();
      })
      .filter(Boolean)
  ), [task?.targets, selectedType]);

  const parsedLines = useMemo<ParsedLine[]>(() => {
    const seenInPaste = new Set<string>();
    return rawText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((text) => {
        const key = text.toLowerCase();
        const isDuplicate = existingValues.has(key) || seenInPaste.has(key);
        if (!isDuplicate) seenInPaste.add(key);
        return { text, type: selectedType, isDuplicate };
      });
  }, [rawText, selectedType, existingValues]);

  const newLines = parsedLines.filter((l) => !l.isDuplicate);
  const dupCount = parsedLines.length - newLines.length;

  const handleAdd = () => {
    if (!newLines.length) return;
    pasteTargets(taskId, selectedType, rawText);
    setRawText('');
    closePasteModal();
  };

  const handleClose = () => closePasteModal();
  const isFieldResourcePicker = selectedType === 'FIELD_RESOURCE';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: "none",
          width: '100%',
          maxWidth: isFieldResourcePicker ? '960px' : '720px',
          mx: '16px',
        },
      }}
    >
      <Box sx={{ px: '20px', pt: '16px', pb: '8px', fontSize: '14px', fontWeight: 600, color: tokens.color.text.primary }}>
        Add Objectives
      </Box>

      <DialogContent sx={{ pt: '8px !important' }}>
        {/* Type selector — hidden when only one option */}
        {allowedTypes.length > 1 && (
          <Box sx={{ mb: '14px' }}>
            <label style={{ fontSize: '11px', fontWeight: 700, color: tokens.color.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Type
            </label>
            <Box sx={{ display: 'flex', gap: '6px', mt: '6px', flexWrap: 'wrap' }}>
              {allowedTypes.map((type) => {
                const isActive = selectedType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedType(type)}
                    style={{
                      fontSize: '12px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? tokens.color.surface.default : tokens.color.text.secondary,
                      backgroundColor: isActive ? PRIMARY_COLOR : tokens.color.surface.subtle,
                      border: isActive ? `1px solid ${PRIMARY_COLOR}` : `1px solid ${tokens.color.border.default}`,
                      borderRadius: '6px',
                      padding: '4px 12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {TARGET_TYPE_LABELS[type]}
                  </button>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Content area */}
        {isFieldResourcePicker ? (
          <FieldResourcePicker taskId={taskId} viewMode={viewMode} setViewMode={setViewMode} />
        ) : (
          <PasteArea
            selectedType={selectedType}
            rawText={rawText}
            onRawTextChange={setRawText}
            parsedLines={parsedLines}
            onAdd={handleAdd}
          />
        )}

        {/* Count row */}
        {!isFieldResourcePicker && parsedLines.length > 0 && (
          <Box sx={{ mt: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: tokens.color.text.primary }}>
              <strong style={{ color: tokens.color.text.primary }}>{newLines.length}</strong> new
            </span>
            {dupCount > 0 && (
              <>
                <span style={{ fontSize: '12px', color: tokens.color.border.strong }}>·</span>
                <span style={{ fontSize: '12px', color: tokens.color.text.secondary }}>
                  <strong style={{ color: tokens.color.text.secondary }}>{dupCount}</strong> duplicate{dupCount > 1 ? 's' : ''}
                </span>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
        <button
          type="button"
          onClick={handleClose}
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: tokens.color.text.secondary,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: '7px',
            padding: '7px 20px',
            cursor: 'pointer',
          }}
        >
          {isFieldResourcePicker ? 'Done' : 'Close'}
        </button>
        {!isFieldResourcePicker && (
          <button
            type="button"
            onClick={handleAdd}
            disabled={newLines.length === 0}
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: tokens.color.surface.default,
              backgroundColor: newLines.length > 0 ? PRIMARY_COLOR : tokens.color.text.secondary,
              border: 'none',
              borderRadius: '7px',
              padding: '7px 20px',
              cursor: newLines.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {newLines.length > 0
              ? `Add ${newLines.length} Objective${newLines.length > 1 ? 's' : ''}`
              : 'Add Objectives'}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PasteTargetsModal;
